import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Wand2, Rainbow, Rocket, Crown, Sparkles, BookOpen, ArrowLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

const FAQItem = ({ question, answer, isOpen, toggleOpen }) => {
  return (
    <motion.div 
      className="border-b border-[#E6EFFD] last:border-0 py-6"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
    >
      <button 
        className="flex justify-between items-center w-full text-left"
        onClick={toggleOpen}
      >
        <h3 className="text-xl font-bold text-[#5E60CE] font-display pr-8">{question}</h3>
        <div className="p-2 bg-[#F0F7FF] rounded-full flex-shrink-0">
          {isOpen ? (
            <ChevronUp className="h-5 w-5 text-[#4F86F7]" />
          ) : (
            <ChevronDown className="h-5 w-5 text-[#4F86F7]" />
          )}
        </div>
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="pt-4 pr-10">
              <p className="text-gray-600 font-display leading-relaxed">{answer}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const FAQ = () => {
  // FAQ data with state
  const [faqs, setFaqs] = useState([
    {
      id: 1,
      question: "How does kuchipuchi create personalized stories?",
      answer: "kuchipuchi uses advanced AI to craft unique stories based on your child's name, age, and preferences. Our magical story generator combines creative storytelling with beautiful illustrations to create a one-of-a-kind story that your child will love.",
      isOpen: false
    },
    {
      id: 2,
      question: "Is kuchipuchi suitable for all age groups?",
      answer: "Yes! kuchipuchi creates stories appropriate for children of all ages. You can select age-appropriate themes and content, with options ranging from simple tales for toddlers to more complex adventures for older children.",
      isOpen: false
    },
    {
      id: 3,
      question: "How long does it take to create a story?",
      answer: "Most personalized stories are generated within minutes! Our magical AI works quickly to craft your unique tale and illustrations, so you won't have to wait long before enjoying your story.",
      isOpen: false
    },
    {
      id: 4,
      question: "Can I edit the story after it's created?",
      answer: "Absolutely! You can make adjustments to your story, modify characters, change plot elements, and even update illustrations to make it perfect for your little one.",
      isOpen: false
    },
    {
      id: 5,
      question: "How can I share the stories with family and friends?",
      answer: "Once your story is created, you can easily share it digitally via email or social media. You can also download a PDF version for printing at home or order a professionally printed physical book to be delivered to your doorstep.",
      isOpen: false
    },
    {
      id: 6,
      question: "Are there educational stories available?",
      answer: "Yes! We offer a wide range of educational stories covering topics like numbers, letters, emotions, cultures, science, and more. These stories make learning fun and engaging for your child.",
      isOpen: false
    },
    {
      id: 7,
      question: "Is my child's information safe and private?",
      answer: "We take privacy very seriously. All personal information is securely stored and never shared with third parties. We only use your child's information to personalize their story experience.",
      isOpen: false
    }
  ]);

  // Toggle FAQ open/closed state
  const toggleFaq = (id) => {
    setFaqs(faqs.map(faq => 
      faq.id === id ? { ...faq, isOpen: !faq.isOpen } : faq
    ));
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-gradient-to-b from-[#E3F2FD] to-white pt-16 pb-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-[#5E60CE] mb-6 font-display">Frequently Asked Questions</h1>
            <p className="text-xl text-[#4F86F7] mb-4 font-display">Find answers to common questions about kuchipuchi</p>
            <div className="w-20 h-1 bg-gradient-to-r from-[#FF6B6B] to-[#FF9AA2] mx-auto rounded-full"></div>
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-500 font-display mt-6">
              <Link to="/" className="hover:text-[#5E60CE]">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <span>FAQs</span>
            </div>
          </motion.div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-3xl mx-auto">
          {/* Decorative elements */}
          <div className="absolute -left-10 top-1/4 text-6xl opacity-20 hidden lg:block">📚</div>
          <div className="absolute -right-10 bottom-1/4 text-6xl opacity-20 hidden lg:block">✨</div>
          
          {/* FAQ List */}
          <div className="bg-white rounded-[24px] shadow-lg p-6 sm:p-8 border border-[#E6EFFD] relative">
            {faqs.map((faq) => (
              <FAQItem 
                key={faq.id}
                question={faq.question}
                answer={faq.answer}
                isOpen={faq.isOpen}
                toggleOpen={() => toggleFaq(faq.id)}
              />
            ))}
          </div>
          
          {/* Still have questions */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mt-16"
          >
            <h2 className="text-2xl font-bold text-[#5E60CE] mb-4 font-display">Still have questions?</h2>
            <p className="text-gray-600 mb-6 font-display">
              Our friendly story wizards are here to help! Write to us at{' '}
              <a 
                href="mailto:support@kuchipuchi.com" 
                className="text-[#FF6B6B] font-medium hover:underline"
              >
                support@kuchipuchi.com
              </a>
            </p>
          </motion.div>
        </div>
      </div>
      
      {/* Decorative bottom */}
      <div className="py-10 bg-[#F8FBFF]">
        <div className="container mx-auto px-4">
          <div className="flex justify-center space-x-8">
            {[
              { icon: Wand2, color: "text-[#5E60CE]" },
              { icon: Rainbow, color: "text-[#FF9AA2]" },
              { icon: Rocket, color: "text-[#4F86F7]" },
              { icon: Crown, color: "text-[#FFD54F]" },
              { icon: Sparkles, color: "text-[#81C784]" },
              { icon: BookOpen, color: "text-[#5E60CE]" }
            ].map((item, index) => (
              <motion.div
                key={index}
                animate={{ y: [0, -10, 0] }}
                transition={{ 
                  duration: 2,
                  delay: index * 0.5,
                  repeat: Infinity,
                  repeatDelay: 1
                }}
                className="opacity-70"
              >
                <item.icon className={`h-8 w-8 ${item.color}`} />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ; 